/**
 * Search Performance API service — GSC metrics endpoints.
 */
import { apiClient } from './apiClient';
import { useTenantStore } from '@/stores/useTenantStore';

const getTenantId = () => useTenantStore.getState().currentTenant;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PageMetric {
  page_url: string;
  blog_content_id: string | null;
  keyword: string | null;
  blog_title: string | null;
  impressions: number;
  clicks: number;
  ctr: number;
  position: number;
}

export interface PageMetricsResponse {
  pages: PageMetric[];
  total: number;
  limit: number;
  offset: number;
}

export interface AggregateMetrics {
  total_impressions: number;
  total_clicks: number;
  avg_ctr: number;
  avg_position: number;
  impressions_change_pct: number;
  clicks_change_pct: number;
  ctr_change: number;
  position_change: number;
  /** Clicks on pages joined to generated articles (blog_content_id). Null = no rows in-window. */
  content_clicks: number | null;
  /** Distinct content-joined pages. Null = no rows in-window. */
  content_pages: number | null;
  /**
   * Clicks from GSC-native branded queries (B2/G-O1). Top-query SAMPLE only —
   * branded + nonbranded will not equal total_clicks. Null = unclassifiable
   * (brand terms not seeded at sync time or native classification unavailable);
   * NEVER 0 as a substitute for "not classified".
   */
  branded_clicks: number | null;
  /** Clicks from classified non-branded queries. Same null semantics and sample caveat as branded_clicks. */
  nonbranded_clicks: number | null;
  period_start: string;
  period_end: string;
}

export interface DailyMetric {
  date: string;
  impressions: number;
  clicks: number;
  ctr: number;
  position: number;
}

export interface DailyMetricsResponse {
  period_start: string;
  period_end: string;
  days: { date: string; clicks: number; impressions: number }[];
}

export interface QueryMetric {
  query: string;
  impressions: number;
  clicks: number;
  ctr: number;
  position: number;
}

export interface PageDetail {
  page_url: string;
  daily_series: DailyMetric[];
  top_queries: QueryMetric[];
  blog_content: {
    id: string;
    keyword: string;
    title: string;
  } | null;
}

export interface SyncStatus {
  status: string;
  sync_started_at: string | null;
  sync_completed_at: string | null;
  rows_synced: number;
  error_message: string | null;
  date_range_start: string | null;
  date_range_end: string | null;
}

export interface GSCSite {
  site_url: string;
  permission_level: string;
}

export type Period = '7d' | '28d' | '90d';

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

export async function fetchPageMetrics(
  tenantId: string | null | undefined,
  period: Period = '28d',
  sortBy: string = 'impressions',
  sortDir: string = 'desc',
  limit: number = 50,
  offset: number = 0,
): Promise<PageMetricsResponse> {
  const { data } = await apiClient.get<PageMetricsResponse>(
    '/search-performance/metrics',
    {
      params: {
        tenant_id: tenantId ?? getTenantId(),
        period,
        sort_by: sortBy,
        sort_dir: sortDir,
        limit,
        offset,
      },
    },
  );
  return data;
}

export async function fetchAggregateMetrics(
  tenantId: string | null | undefined,
  period: Period = '28d',
): Promise<AggregateMetrics> {
  const { data } = await apiClient.get<AggregateMetrics>(
    '/search-performance/aggregate',
    {
      params: {
        tenant_id: tenantId ?? getTenantId(),
        period,
      },
    },
  );
  return data;
}

export async function fetchDailyMetrics(
  tenantId: string | null | undefined,
  period: Period = '28d',
): Promise<DailyMetricsResponse> {
  const { data } = await apiClient.get<DailyMetricsResponse>(
    '/search-performance/daily',
    {
      params: {
        tenant_id: tenantId ?? getTenantId(),
        period,
      },
    },
  );
  return data;
}

export async function fetchPageDetail(
  tenantId: string | null | undefined,
  pageUrl: string,
  period: Period = '28d',
): Promise<PageDetail> {
  const { data } = await apiClient.get<PageDetail>(
    '/search-performance/page-detail',
    {
      params: {
        tenant_id: tenantId ?? getTenantId(),
        page_url: pageUrl,
        period,
      },
    },
  );
  return data;
}

export async function triggerSync(
  tenantId: string | null | undefined,
  daysBack: number = 28,
): Promise<{ status: string; rows_synced?: number; error?: string }> {
  const { data } = await apiClient.post('/search-performance/sync', null, {
    params: {
      tenant_id: tenantId ?? getTenantId(),
      days_back: daysBack,
    },
  });
  return data;
}

export async function fetchSyncStatus(
  tenantId: string | null | undefined,
): Promise<SyncStatus> {
  const { data } = await apiClient.get<SyncStatus>(
    '/search-performance/sync-status',
    {
      params: { tenant_id: tenantId ?? getTenantId() },
    },
  );
  return data;
}

export async function fetchGSCSites(): Promise<GSCSite[]> {
  const { data } = await apiClient.get<{ sites: GSCSite[] }>(
    '/search-performance/sites',
  );
  return data.sites;
}
